import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import MarketingSeo from "../../components/marketing/MarketingSeo";
import ButtonLink from "../../components/marketing/ButtonLink";
import { MarketingMotion } from "../../components/marketing/MarketingMotion";
import { MarketingTopBar, SiteFooter, requestBookDemo } from "../../components/marketing/shared";
import { CTA_PRIMARY_LABEL } from "../../components/marketing/navigation";
import { Button, Card } from "../../components/ui/primitives";
import { init as initAnalytics, track } from "../../lib/analytics";
import { ProductStoryPicture } from "./SolutionProductShowcase";
import { PRODUCT_STORY_ASSETS } from "./solutionProductEvidence";
import { SolutionReveal, SolutionStagger, SolutionStaggerItem } from "./SolutionMotion";
import {
  SOLUTION_HUB_SEO,
  SOLUTION_KEYS,
  SOLUTION_REGISTRY,
  SOLUTION_TRUST_POINTS,
} from "./solutionRegistry";
import "./solution-pages.css";

const DELIVERY_STAGES = Object.freeze([
  ["Fit review", "Map the institution, current workflows, provider dependencies and product boundaries."],
  ["Workflow review", "Walk the highest-risk scenarios through product walkthroughs and verified outputs before committing scope."],
  ["Governed configuration", "Set roles, imports, approvals, templates and providers with named owners."],
  ["Staged release", "Train and verify one accepted workflow before expanding to the next stage."],
]);

function SolutionPreviewMedia({ media }) {
  const avif = media.widths.map((width) => `${media.base}.w${width}.avif ${width}w`).join(", ");
  const webp = media.widths.map((width) => `${media.base}.w${width}.webp ${width}w`).join(", ");
  const fallbackWidth = media.widths.find((width) => width >= 960) || media.widths[media.widths.length - 1];
  return (
    <picture>
      <source type="image/avif" srcSet={avif} sizes="(max-width: 640px) 100vw, 50vw" />
      <source type="image/webp" srcSet={webp} sizes="(max-width: 640px) 100vw, 50vw" />
      <img
        alt={media.alt}
        decoding="async"
        height={media.height}
        loading="lazy"
        src={`${media.base}.w${fallbackWidth}.webp`}
        width={media.width}
      />
    </picture>
  );
}

function HubSectionHeading({ eyebrow, id, title, description }) {
  return (
    <div className="solution-section-heading">
      <p>{eyebrow}</p>
      <h2 id={id}><BrandCopy>{title}</BrandCopy></h2>
      {description ? <div><BrandCopy>{description}</BrandCopy></div> : null}
    </div>
  );
}

export default function SolutionsHubPage() {
  React.useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: "Vidyapeeth360 solutions",
        url: "https://vidyapeeth360.com/solutions",
        description: SOLUTION_HUB_SEO.description,
      },
      {
        "@type": "ItemList",
        itemListElement: SOLUTION_KEYS.map((key, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: SOLUTION_REGISTRY[key].badge,
          url: `https://vidyapeeth360.com/solutions/${key}`,
        })),
      },
    ],
  };

  return (
    <MarketingMotion>
      <div className="min-h-screen bg-surface landing-page solution-page" data-testid="solutions-hub">
        <MarketingSeo
          title={SOLUTION_HUB_SEO.title}
          description={SOLUTION_HUB_SEO.description}
          path="/solutions"
          schema={schema}
        />
        <MarketingTopBar />
        <main id="main" tabIndex="-1">
          <section className="solutions-hub-hero" aria-labelledby="solutions-hub-title">
            <div className="solutions-hub-hero__inner">
              <SolutionStagger as="div" play="mount" stagger={0.06} delay={0.04}>
                <SolutionStaggerItem as="p" className="solutions-hub-kicker">Solutions by operating model</SolutionStaggerItem>
                <SolutionStaggerItem as="h1" id="solutions-hub-title"><BrandCopy>Start with the institution you actually run.</BrandCopy></SolutionStaggerItem>
              </SolutionStagger>
              <SolutionStagger as="div" className="solutions-hub-hero__aside" play="mount" stagger={0.06} delay={0.12}>
                <SolutionStaggerItem as="p">
                  Compare connected workflows, product walkthroughs and rollout boundaries for schools,
                  preschools, colleges and coaching institutes. Each route says what is ready, what
                  depends on configuration and what still needs a fit review.
                </SolutionStaggerItem>
                <SolutionStaggerItem>
                  <ButtonLink
                    to="/register"
                    size="lg"
                    onClick={() => track("trial_cta_click", { placement: "solutions_hub_hero" })}
                  >
                    {CTA_PRIMARY_LABEL} <ArrowRight aria-hidden="true" />
                  </ButtonLink>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={() => requestBookDemo({ placement: "solutions_hub_hero", contextLabel: "solution fit" })}
                  >
                    Review your fit
                  </Button>
                </SolutionStaggerItem>
              </SolutionStagger>
            </div>
          </section>

          <section className="solutions-hub-product" aria-labelledby="solutions-product-title">
            <div className="solutions-hub-product__inner">
              <SolutionReveal className="solutions-hub-product__copy">
                <div>
                  <p>One connected operating layer</p>
                  <h2 id="solutions-product-title"><BrandCopy>See the day, the work and the next decision in one place.</BrandCopy></h2>
                </div>
                <div>
                  <p>Start with a role-aware overview, then move into the workflow that needs attention without losing institutional context.</p>
                  <Link to="/modules">Explore the product system <ArrowRight aria-hidden="true" /></Link>
                </div>
              </SolutionReveal>
              <SolutionReveal className="solutions-hub-product__frame" variant="scale" amount={0.14}>
                <div className="solution-product-browser__chrome" aria-hidden="true">
                  <span /><span /><span />
                  <b>Vidyapeeth360 workspace</b>
                  <small>Product walkthrough</small>
                </div>
                <ProductStoryPicture image={PRODUCT_STORY_ASSETS.overview} />
              </SolutionReveal>
            </div>
          </section>

          <section className="solutions-hub-section" aria-labelledby="solutions-audiences-title">
            <HubSectionHeading
              eyebrow="Choose your route"
              id="solutions-audiences-title"
              title="Four operating contexts. One evidence-aware way to evaluate them."
              description="Each route changes the workflow emphasis, product views and rollout plan so the page speaks to the institution your team operates every day."
            />
            <SolutionStagger as="div" className="solutions-hub-grid" stagger={0.06} amount={0.1}>
              {SOLUTION_KEYS.map((key) => {
                const solution = SOLUTION_REGISTRY[key];
                const Icon = solution.icon;
                return (
                  <SolutionStaggerItem key={key}>
                    <Card as="article" className="solutions-hub-card">
                      <SolutionPreviewMedia media={solution.media} />
                      <div className="solutions-hub-card__body">
                        <div>
                          <h3>{solution.badge}</h3>
                          <Icon aria-hidden="true" />
                        </div>
                        <p>{solution.subhead}</p>
                        <Link to={`/solutions/${key}`} aria-label={`Explore the ${solution.badge} solution`}>
                          Explore solution <ArrowRight aria-hidden="true" />
                        </Link>
                      </div>
                    </Card>
                  </SolutionStaggerItem>
                );
              })}
            </SolutionStagger>
          </section>

          <section className="solutions-hub-section" aria-labelledby="solutions-standards-title">
            <HubSectionHeading
              eyebrow="Evaluation standards"
              id="solutions-standards-title"
              title="Clarity is part of the product promise."
              description="A solution page should help an institution disqualify a poor fit as confidently as it helps validate a good one."
            />
            <div className="solution-fit-grid">
              {SOLUTION_TRUST_POINTS.map((item, index) => (
                <Card as="article" className="solution-fit-card" key={item.label}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <h3><item.icon aria-hidden="true" /> {item.label}</h3>
                  <p>{item.detail}</p>
                </Card>
              ))}
            </div>
          </section>

          <section className="solutions-hub-section solutions-hub-method" aria-labelledby="solutions-method-title">
            <HubSectionHeading
              eyebrow="How a rollout earns confidence"
              id="solutions-method-title"
              title="Reduce uncertainty at every stage."
              description="The sequence stays deliberately simple: map the operating reality, inspect evidence, configure boundaries and release in accepted stages."
            />
            <ol>
              {DELIVERY_STAGES.map(([title, description], index) => (
                <li key={title}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="solution-closing" aria-labelledby="solutions-close-title">
            <div>
              <p>Choose with evidence</p>
              <h2 id="solutions-close-title">Bring one real workflow to the review.</h2>
              <p>
                We will use it to confirm record fit, permissions, provider dependencies,
                migration scope and the smallest useful first release.
              </p>
              <div>
                <ButtonLink
                  to="/register"
                  size="lg"
                  className="solution-closing__primary"
                  onClick={() => track("trial_cta_click", { placement: "solutions_hub_close" })}
                >
                  {CTA_PRIMARY_LABEL} <ArrowRight aria-hidden="true" />
                </ButtonLink>
                <Button
                  size="lg"
                  variant="ghost"
                  className="solution-closing__secondary"
                  onClick={() => requestBookDemo({ placement: "solutions_hub_close", contextLabel: "workflow review" })}
                >
                  Book a workflow review
                </Button>
              </div>
            </div>
          </section>
        </main>
        <SiteFooter />
      </div>
    </MarketingMotion>
  );
}
